"use client";

import type { ReactNode } from "react";
import Modal from "@/components/ui/Modal";
import Button from "@/components/ui/Button";
import { categoryLabels } from "@/lib/categoryLabels";
import type { ItemCategory } from "@/lib/types";
import {
  formatSellFormPickupWindow,
  SELL_FORM_DAYS_OF_WEEK,
} from "@/lib/listing/sellFormConstants";
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import type { BulkListingCommonState } from "@/lib/listing/bulkListingTypes";
import type { BulkListingItemDraft } from "@/lib/listing/bulkListingTypes";
import type { CategoryStructure } from "@/lib/categoryStructure";

export interface BulkReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  common: BulkListingCommonState;
  items: BulkListingItemDraft[];
  categoryStructure: CategoryStructure[];
  canSubmit: boolean;
  blockingMessages: string[];
  onJumpToItem: (index: number) => void;
  onConfirmSubmit: () => void;
  isSubmitting: boolean;
  submitProgress: { current: number; total: number } | null;
  submitFinished: boolean;
  successCount: number;
  failures: Array<{ id: string; title: string; message: string }>;
  onRetryFailed: () => void;
  /** Per-item: passes full validation (for status icons in the list). */
  itemsReady?: boolean[];
}

function detailRow(label: string, value: ReactNode) {
  return (
    <div className="flex flex-col gap-0.5 border-b border-border py-2 last:border-0">
      <dt className="text-xs font-medium uppercase tracking-wide text-text-light">
        {label}
      </dt>
      <dd className="text-sm text-foreground">{value}</dd>
    </div>
  );
}

export default function BulkReviewModal({
  isOpen,
  onClose,
  common,
  items,
  categoryStructure,
  canSubmit,
  blockingMessages,
  onJumpToItem,
  onConfirmSubmit,
  isSubmitting,
  submitProgress,
  submitFinished,
  successCount,
  failures,
  onRetryFailed,
  itemsReady = [],
}: BulkReviewModalProps) {
  const selectedDays = common.pickupDays
    .slice()
    .sort((a, b) => a - b)
    .map((day) => SELL_FORM_DAYS_OF_WEEK.find((d) => d.value === day)?.label)
    .join(", ");

  const subcategoryName = (categoryId: string, subId: string) => {
    if (!categoryId) return "—";
    return (
      categoryStructure
        .find((c) => c.id === categoryId)
        ?.subcategories.find((s) => s.id === subId)?.name ?? subId
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Review & submit"
      maxWidth="2xl"
      hideFooter
      closeOnClickOutside={!isSubmitting}
    >
      <div className="space-y-6 max-h-[65vh] overflow-y-auto pr-1">
        {!submitFinished && (
          <>
            <section className="rounded-lg border border-border bg-secondary/80 p-4">
              <h3 className="text-sm font-semibold text-foreground mb-2">
                Shared location &amp; pickup
              </h3>
              <dl>
                {detailRow("Location", common.location || "—")}
                {detailRow(
                  "Pickup days",
                  selectedDays.length > 0 ? selectedDays : "Not set (optional)",
                )}
                {detailRow(
                  "Pickup hours",
                  formatSellFormPickupWindow(
                    common.pickupStartHour,
                    common.pickupEndHour,
                  ),
                )}
              </dl>
            </section>

            {blockingMessages.length > 0 && (
              <div
                className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
                role="alert"
              >
                <p className="font-medium mb-1">
                  Almost there — fix these first:
                </p>
                <ul className="list-disc pl-5 space-y-1">
                  {blockingMessages.map((msg, i) => (
                    <li key={`${i}-${msg.slice(0, 40)}`}>{msg}</li>
                  ))}
                </ul>
              </div>
            )}

            <section className="space-y-3">
              <h3 className="text-sm font-semibold text-foreground">
                Your listings
              </h3>
              <ul className="space-y-2">
                {items.map((it, index) => (
                  <li
                    key={it.id}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border p-3 bg-surface"
                  >
                    <div className="flex min-w-0 flex-1 gap-2">
                      <span className="shrink-0 mt-0.5" aria-hidden>
                        {(itemsReady[index] ?? false) ? (
                          <CheckCircleIcon
                            className="h-5 w-5 text-primary-600"
                            aria-hidden
                          />
                        ) : (
                          <ExclamationTriangleIcon
                            className="h-5 w-5 text-amber-500"
                            aria-hidden
                          />
                        )}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-foreground truncate">
                          {it.title.trim() || `Item ${index + 1} (no title)`}
                        </p>
                        <p className="text-xs text-text-light">
                          {it.category
                            ? categoryLabels[it.category as ItemCategory] ||
                              it.category
                            : "—"}{" "}
                          · {subcategoryName(it.category, it.subcategory)} · ₦
                          {it.price || "—"}
                        </p>
                        {it.submitError ? (
                          <p className="text-xs text-red-600 mt-1">
                            {it.submitError}
                          </p>
                        ) : null}
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="secondary"
                      className="shrink-0"
                      onClick={() => {
                        onJumpToItem(index);
                        onClose();
                      }}
                      disabled={isSubmitting}
                    >
                      Edit
                    </Button>
                  </li>
                ))}
              </ul>
            </section>

            {isSubmitting && submitProgress ? (
              <div
                className="rounded-lg border border-primary-200 bg-primary-50/90 px-4 py-3 text-sm text-primary-900"
                aria-live="polite"
              >
                <p className="font-medium">
                  Creating listing {submitProgress.current} of{" "}
                  {submitProgress.total}
                </p>
                <p className="text-primary-800/90 text-xs mt-1">
                  Please keep this tab open until we&apos;re done.
                </p>
              </div>
            ) : null}
          </>
        )}

        {submitFinished && (
          <div className="space-y-4">
            <p className="text-sm text-foreground">
              {successCount === items.length ? (
                <>
                  <span className="font-semibold text-primary-800">
                    All set — {successCount} listing
                    {items.length === 1 ? "" : "s"} created.
                  </span>{" "}
                  Check your email for updates while we review.
                </>
              ) : (
                <>
                  Created {successCount} of {items.length} listing
                  {items.length === 1 ? "" : "s"} successfully. You can retry
                  the rest below.
                </>
              )}
            </p>
            {failures.length > 0 ? (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                <p className="text-sm font-medium text-red-900 mb-2">
                  Failed ({failures.length})
                </p>
                <ul className="text-sm text-red-800 space-y-2">
                  {failures.map((f) => (
                    <li key={f.id}>
                      <span className="font-medium">
                        {f.title || "Untitled"}
                      </span>
                      : {f.message}
                    </li>
                  ))}
                </ul>
                <Button
                  type="button"
                  className="mt-3"
                  onClick={onRetryFailed}
                  haptic="light"
                >
                  Retry failed
                </Button>
              </div>
            ) : null}
          </div>
        )}
      </div>

      <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-border">
        {!submitFinished ? (
          <>
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={onConfirmSubmit}
              disabled={!canSubmit || isSubmitting}
              aria-busy={isSubmitting}
              haptic="light"
            >
              {isSubmitting
                ? "Creating listings…"
                : canSubmit
                  ? `Submit ${items.length} listing${items.length === 1 ? "" : "s"}`
                  : "Fix issues to submit"}
            </Button>
          </>
        ) : (
          <Button type="button" onClick={onClose}>
            Close
          </Button>
        )}
      </div>
    </Modal>
  );
}
